import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoIosCheckmarkCircle, IoMdArrowBack } from "react-icons/io";
import { updateDateOfBirth, getDateOfBirth } from "../../redux/ApiSlice/AccountSettingSlice";

const normalizeDOB = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
        if (value.date_of_birth) return normalizeDOB(value.date_of_birth);
        if (value.date) return normalizeDOB(value.date);
        if (typeof value.year === "number" && typeof value.month === "number" && typeof value.day === "number") {
            const pad = (num) => String(num).padStart(2, "0");
            return `${value.year}-${pad(value.month)}-${pad(value.day)}`;
        }
        return "";
    }
    return String(value);
};

const formatToMMDDYYYY = (value) => {
    const v = normalizeDOB(value);
    if (!v) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [yyyy, mm, dd] = v.split("-");
        return `${mm}-${dd}-${yyyy}`;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return v;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
        const [mm, dd, yyyy] = v.split("/");
        return `${mm}-${dd}-${yyyy}`;
    }
    return v;
};

const formatDobInput = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    const parts = [];
    if (digits.length >= 2) {
        parts.push(digits.slice(0, 2));
    } else {
        parts.push(digits);
    }
    if (digits.length >= 4) {
        parts.push(digits.slice(2, 4));
    } else if (digits.length > 2) {
        parts.push(digits.slice(2));
    }
    if (digits.length > 4) {
        parts.push(digits.slice(4));
    }
    return parts.filter(Boolean).join("-");
};

const formatMessage = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") {
        if (value.detail) return formatMessage(value.detail);
        if (value.date_of_birth) return formatMessage(value.date_of_birth);
        const all = Object.values(value).map((item) => formatMessage(item)).filter(Boolean);
        return all.join(", ");
    }
    return String(value);
};

const isValidDate = (mmddyyyy) => {
    const [mm, dd, yyyy] = mmddyyyy.split("-").map(Number);
    const month = mm;
    const day = dd;
    const year = yyyy;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return false;
    if (date >= new Date()) return false;
    return true;
};

const AccountDOB = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { dateOfBirthData, dateOfBirthLoading, dateOfBirthError } = useSelector((state) => state.AccountSetting);

    const [newDob, setNewDob] = useState("");
    const [localError, setLocalError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        dispatch(getDateOfBirth());
    }, [dispatch]);

    const verifiedDOB = useMemo(
        () => formatToMMDDYYYY(dateOfBirthData?.date_of_birth) || "Not set",
        [dateOfBirthData]
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLocalError("");
        setSuccessMessage("");

        const trimmedDob = newDob.trim();
        const dobPattern = /^\d{2}-\d{2}-\d{4}$/;

        if (!dobPattern.test(trimmedDob)) {
            setLocalError("Enter a valid date in MM-DD-YYYY format.");
            return;
        }

        if (!isValidDate(trimmedDob)) {
            setLocalError("Enter a real date that is not in the future.");
            return;
        }

        try {
            await dispatch(updateDateOfBirth({ date_of_birth: trimmedDob })).unwrap();
            setSuccessMessage("Date of birth updated successfully.");
            dispatch(getDateOfBirth());
            setNewDob("");
        } catch (error) {
            const message =
                error?.detail ||
                error?.message ||
                (typeof error === "string" ? error : "Failed to update date of birth.");
            setLocalError(message);
        }
    };

    return (
        <div className="bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0 z-50">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/settings/account/details")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">
                        Date of Birth
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="px-3 flex flex-col justify-between h-[calc(80vh-60px)]">
                <div className="flex flex-col pt-5">
                    <p className="text-[14px] text-black dark:text-white font-bold">Date of Birth</p>
                    <p className="text-[12px] text-justify text-black dark:text-white pt-1">
                        Your birthday helps us protect your account and tailor your experience. You can change your birth date only once.
                    </p>

                    <div className="mt-5 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg">
                        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#BDE6FF] dark:border-gray-800/50">
                            <div className="flex flex-col">
                                <p className="text-[12px] text-black dark:text-white font-bold">Verified birthday</p>
                                <p className="w-full text-[12px] text-black dark:text-white">{verifiedDOB}</p>
                            </div>
                            <IoIosCheckmarkCircle className="text-[22px] text-[#4cb6f9]" />
                        </div>
                        <div className="px-5 py-4">
                            <p className="text-[12px] text-black dark:text-white font-bold">Change your birthday</p>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                placeholder="MM-DD-YYYY"
                                value={newDob}
                                onChange={(e) => setNewDob(formatDobInput(e.target.value))}
                                className="w-full text-[12px] bg-white dark:bg-[#1E1D21] text-black dark:text-white outline-none"
                            />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    {(localError || dateOfBirthError) && (
                        <p className="text-[12px] text-red-600">{formatMessage(localError || dateOfBirthError)}</p>
                    )}
                    {successMessage && !localError && !dateOfBirthError && (
                        <p className="text-[12px] text-green-600">{successMessage}</p>
                    )}
                    <button
                        type="submit"
                        disabled={dateOfBirthLoading}
                        className="text-[13px] w-full bg-[#BDE6FF] text-black font-bold py-3 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {dateOfBirthLoading ? "Saving..." : "Save changes"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AccountDOB;
